<template>
  <Loader v-if="loading" />

  <div class="page-container">
    <div class="flex align-items-center justify-content-between flex-wrap gap-3 mb-4">
      <div>
        <h1 class="m-0 text-3xl font-semibold text-primary"><i class="pi pi-id-card mr-2"></i>Tesseramenti FIGC</h1>
        <p class="text-color-secondary mt-1 mb-0">Carica l'export dei tesserati dal portale FIGC. Ogni stagione viene salvata separatamente: lo storico resta e chi non rinnova semplicemente non avrà la tessera della nuova stagione.</p>
      </div>
      <div class="flex align-items-center gap-2">
        <label for="anno" class="font-medium">Stagione</label>
        <Dropdown id="anno" v-model="anno" :options="stagioniOptions" optionLabel="label" optionValue="value" @change="fetchData" style="min-width: 160px" />
      </div>
    </div>

    <div class="grid mb-4">
      <!-- Upload -->
      <div class="col-12 md:col-6">
        <Card class="h-full border-1 border-200 shadow-2">
          <template #title>
            <div class="flex align-items-center gap-2 text-primary">
              <i class="pi pi-cloud-upload text-xl"></i>
              <h3 class="m-0 text-lg">Carica file FIGC (.xls / .xlsx / .csv)</h3>
            </div>
          </template>
          <template #content>
            <div
              class="upload-area border-2 border-dashed border-primary border-round-xl p-4 text-center cursor-pointer hover:surface-hover flex flex-column align-items-center justify-content-center"
              style="min-height: 140px;"
              @click="fileInput.click()"
              @dragover.prevent
              @drop.prevent="onDrop"
            >
              <i class="pi pi-file-excel text-4xl text-primary mb-2"></i>
              <span class="font-medium">Clicca o trascina qui il file</span>
              <small class="text-color-secondary mt-1">
                Prima del salvataggio viene mostrata un'anteprima in cui abbinare a mano i tesserati non riconosciuti.
                Se manca "Anno Scadenza Contratto" verrà usata la stagione {{ labelStagione(anno) }}.
              </small>
            </div>
            <input ref="fileInput" type="file" accept=".xls,.xlsx,.csv" style="display:none" @change="onFileSelected" />
          </template>
        </Card>
      </div>

      <!-- Riepilogo stagioni -->
      <div class="col-12 md:col-6">
        <Card class="h-full border-1 border-200 shadow-2">
          <template #title>
            <div class="flex align-items-center gap-2 text-primary">
              <i class="pi pi-history text-xl"></i>
              <h3 class="m-0 text-lg">Stagioni caricate</h3>
            </div>
          </template>
          <template #content>
            <p v-if="stagioni.length === 0" class="m-0">Nessun tesseramento presente.</p>
            <DataTable v-else :value="stagioni" class="p-datatable-sm" responsiveLayout="scroll">
              <Column header="Stagione">
                <template #body="{ data }">
                  <a href="#" @click.prevent="selectAnno(data.anno)" :class="{ 'font-bold': Number(data.anno) === anno }">{{ labelStagione(data.anno) }}</a>
                </template>
              </Column>
              <Column field="tesserati" header="Tesserati" />
              <Column header="Ultimo caricamento">
                <template #body="{ data }">{{ data.ultimo_caricamento ? moment(data.ultimo_caricamento).format('DD/MM/YYYY HH:mm') : '-' }}</template>
              </Column>
            </DataTable>
          </template>
        </Card>
      </div>
    </div>

    <TabView>
      <TabPanel :header="`Tesserati ${labelStagione(anno)} (${tesserati.length})`">
        <div class="flex justify-content-between align-items-center flex-wrap gap-2 mb-2">
          <div class="flex align-items-center">
            <Checkbox v-model="soloNonCollegati" inputId="soloNonCollegati" :binary="true" />
            <label for="soloNonCollegati" class="ml-2">Solo non collegati ({{ nonCollegati }})</label>
          </div>
          <span class="p-input-icon-left">
            <i class="pi pi-search" />
            <InputText v-model="search" placeholder="Cerca nome, matricola, CF" />
          </span>
        </div>
        <DataTable :value="tesseratiFiltrati" dataKey="id" class="p-datatable-sm p-datatable-striped" responsiveLayout="scroll" :paginator="true" :rows="50">
          <Column field="Matricola" header="Matricola" sortable />
          <Column field="Nome" header="Nome" sortable />
          <Column field="codicefiscale" header="Codice Fiscale" />
          <Column field="DataNascita" header="Nascita" />
          <Column field="Disciplina" header="Disciplina" sortable />
          <Column field="DataTesseramento" header="Tesseramento" />
          <Column field="Status" header="Status" />
          <Column header="Atleta">
            <template #body="{ data }">
              <div v-if="data.id_atleta" class="flex align-items-center gap-1">
                <Button class="p-button-link p-0" @click="goToAtleta(data.id_atleta)">{{ data.atleta_cognome }} {{ data.atleta_nome }}</Button>
                <i v-if="data.atleta_codfis !== data.codicefiscale" class="pi pi-exclamation-triangle text-orange-500" v-tooltip="`CF in anagrafica diverso: ${data.atleta_codfis || 'mancante'}`"></i>
                <Button icon="pi pi-pencil" class="p-button-rounded p-button-text p-button-sm" v-tooltip="'Cambia abbinamento'" @click="openCollega(data)" />
              </div>
              <Button v-else label="Collega" icon="pi pi-link" class="p-button-warning p-button-sm" @click="openCollega(data)" />
            </template>
          </Column>
          <Column style="width: 60px">
            <template #body="{ data }">
              <Button icon="pi pi-trash" class="p-button-rounded p-button-danger p-button-text" @click="askDelete(data)" />
            </template>
          </Column>
        </DataTable>
      </TabPanel>

      <TabPanel :header="`Iscritti senza tessera (${senzaTessera.length})`">
        <Message severity="info" :closable="false">
          Atleti con un'iscrizione attiva a un corso della stagione {{ labelStagione(anno) }} che non hanno il tesseramento FIGC per questa stagione. Non tutti gli atleti devono essere tesserati: l'elenco è solo informativo.
        </Message>
        <DataTable :value="senzaTessera" dataKey="id" class="p-datatable-sm p-datatable-striped" responsiveLayout="scroll" :paginator="true" :rows="50">
          <Column header="Atleta" sortable sortField="cognome">
            <template #body="{ data }">
              <Button class="p-button-link p-0" @click="goToAtleta(data.id)">{{ data.cognome }} {{ data.nome }}</Button>
            </template>
          </Column>
          <Column field="datadinascita" header="Nascita" />
          <Column field="codfis" header="Codice Fiscale" />
          <Column field="corsi" header="Corsi" />
          <Column header="Ultima tessera" sortable sortField="ultima_stagione">
            <template #body="{ data }">
              <Tag v-if="data.ultima_stagione" severity="warning" :value="labelStagione(data.ultima_stagione)" />
              <span v-else class="text-color-secondary">Mai tesserato</span>
            </template>
          </Column>
        </DataTable>
      </TabPanel>
    </TabView>
  </div>

  <!-- Anteprima import -->
  <Dialog v-model:visible="previewVisible" header="Anteprima importazione FIGC" :modal="true" :style="{ width: '95vw' }" :maximizable="true">
    <div v-if="preview">
      <div class="flex flex-wrap gap-2 mb-3">
        <Tag severity="info" :value="`Stagioni nel file: ${preview.anni.map(labelStagione).join(', ')}`" />
        <Tag severity="success" :value="`Nuovi: ${preview.summary.nuovi}`" />
        <Tag severity="warning" :value="`Aggiornati: ${preview.summary.aggiornati}`" />
        <Tag :value="`Invariati: ${preview.summary.invariati}`" />
        <Tag severity="danger" :value="`Scartati: ${preview.summary.errori}`" />
        <Tag :severity="daAbbinare.length - abbinatiAMano > 0 ? 'warning' : 'success'" :value="`Da abbinare: ${daAbbinare.length} (abbinati a mano: ${abbinatiAMano})`" />
      </div>

      <Message v-if="daAbbinare.length > 0" severity="warn" :closable="false">
        {{ daAbbinare.length }} tesserati non hanno un codice fiscale corrispondente in anagrafica. Per ognuno è proposto l'atleta più probabile (se trovato):
        verifica e conferma l'abbinamento, oppure cerca l'atleta a mano. Con "Correggi CF" il codice fiscale dell'atleta viene sostituito con quello FIGC.
        I tesserati lasciati senza abbinamento vengono comunque salvati e potranno essere collegati in seguito.
      </Message>

      <Message v-if="preview.summary.non_presenti_nel_file > 0" severity="warn" :closable="false">
        Ci sono {{ preview.summary.non_presenti_nel_file }} tesseramenti già salvati per {{ preview.anni.length > 1 ? 'queste stagioni' : 'questa stagione' }} che non sono nel file.
        Di default vengono mantenuti; seleziona l'opzione sotto per eliminarli (es. tessere annullate).
      </Message>
      <div class="flex align-items-center mb-3">
        <Checkbox v-model="sostituisciStagione" inputId="sostituisci" :binary="true" />
        <label for="sostituisci" class="ml-2">Sostituisci la stagione: elimina i tesseramenti della stagione non presenti nel file</label>
      </div>

      <div class="flex justify-content-between align-items-center flex-wrap gap-2 mb-2">
        <SelectButton v-model="filtroStato" :options="filtriStato" optionLabel="label" optionValue="value" />
        <small class="text-color-secondary">Colonne riconosciute: {{ Object.values(preview.colonne_trovate).join(', ') }}</small>
      </div>

      <DataTable :value="previewFiltrata" dataKey="riga" class="p-datatable-sm p-datatable-gridlines" responsiveLayout="scroll" :paginator="true" :rows="25">
        <Column field="riga" header="Riga" style="width: 60px" />
        <Column header="Stato" style="width: 100px">
          <template #body="{ data }">
            <Tag :severity="statoSeverity(data.stato)" :value="data.stato" />
          </template>
        </Column>
        <Column field="Matricola" header="Matricola" />
        <Column header="Tesserato FIGC">
          <template #body="{ data }">
            <div class="font-medium">{{ data.Nome }}</div>
            <small class="text-color-secondary">{{ data.DataNascita }} - {{ data.codicefiscale }}</small>
          </template>
        </Column>
        <Column header="Stagione" style="width: 90px">
          <template #body="{ data }">{{ labelStagione(data.AnnoScadenzaContratto) }}</template>
        </Column>
        <Column field="Disciplina" header="Disciplina" />
        <Column header="Atleta in anagrafica" style="min-width: 420px">
          <template #body="{ data }">
            <span v-if="data.errore" class="text-red-500">{{ data.errore }}</span>

            <span v-else-if="data.id_atleta">
              <i class="pi pi-check-circle text-green-500 mr-1"></i>{{ data.atleta }}
              <Tag v-if="data.match === 'storico'" class="ml-1" severity="info" value="abbinato in stagioni precedenti" />
            </span>

            <div v-else class="flex flex-column gap-1">
              <div class="flex align-items-center gap-2">
                <AutoComplete
                  v-model="data.scelta"
                  :suggestions="atletiSuggeriti"
                  field="label"
                  :dropdown="true"
                  forceSelection
                  placeholder="Cerca atleta (cognome, nome o CF)"
                  class="flex-1"
                  inputClass="w-full"
                  @complete="cercaAtleti($event, data)"
                  @item-select="onSceltaAtleta(data)"
                  @clear="data.scelta = null"
                />
                <Button v-if="data.scelta" icon="pi pi-times" class="p-button-rounded p-button-text p-button-danger p-button-sm" v-tooltip="'Lascia non collegato'" @click="data.scelta = null" />
              </div>
              <small v-if="data.scelta && data.scelta.motivi" class="text-color-secondary">
                <Tag v-if="data.preselezionato" severity="warning" value="suggerito" class="mr-1" />{{ data.scelta.motivi }}
              </small>
              <small v-else-if="!data.scelta && data.suggerimenti.length === 0" class="text-orange-500">Nessun atleta simile trovato: cerca a mano o lascia non collegato</small>
              <div v-if="data.scelta && data.scelta.codfis !== data.codicefiscale" class="flex align-items-center">
                <Checkbox v-model="data.aggiorna_cf" :inputId="`cf_${data.riga}`" :binary="true" />
                <label :for="`cf_${data.riga}`" class="ml-2 text-sm">
                  Correggi CF in anagrafica: <s>{{ data.scelta.codfis || 'mancante' }}</s> → <b>{{ data.codicefiscale }}</b>
                </label>
              </div>
            </div>
          </template>
        </Column>
      </DataTable>
    </div>
    <template #footer>
      <Button label="Annulla" icon="pi pi-times" class="p-button-text" @click="annullaImport" />
      <Button label="Conferma importazione" icon="pi pi-check" :disabled="!preview || validRows === 0" @click="confermaImport" />
    </template>
  </Dialog>

  <!-- Collegamento manuale dopo l'import -->
  <Dialog v-model:visible="collegaDialog.visible" header="Collega tesseramento ad atleta" :modal="true" :style="{ width: '600px' }">
    <div v-if="collegaDialog.item" class="flex flex-column gap-3">
      <div>
        <div class="font-medium">{{ collegaDialog.item.Nome }}</div>
        <small class="text-color-secondary">Matricola {{ collegaDialog.item.Matricola }} - {{ collegaDialog.item.DataNascita }} - {{ collegaDialog.item.codicefiscale }}</small>
      </div>
      <AutoComplete
        v-model="collegaDialog.atleta"
        :suggestions="atletiSuggeriti"
        field="label"
        :dropdown="true"
        forceSelection
        placeholder="Cerca atleta (cognome, nome o CF)"
        inputClass="w-full"
        @complete="cercaAtleti($event, null)"
        @item-select="collegaDialog.aggiorna_cf = collegaDialog.atleta.codfis !== collegaDialog.item.codicefiscale"
      />
      <div v-if="collegaDialog.atleta && collegaDialog.atleta.codfis !== collegaDialog.item.codicefiscale" class="flex align-items-center">
        <Checkbox v-model="collegaDialog.aggiorna_cf" inputId="collegaCf" :binary="true" />
        <label for="collegaCf" class="ml-2">
          Correggi CF in anagrafica: <s>{{ collegaDialog.atleta.codfis || 'mancante' }}</s> → <b>{{ collegaDialog.item.codicefiscale }}</b>
        </label>
      </div>
      <small class="text-color-secondary">L'abbinamento vale per tutte le stagioni di questa matricola e verrà riutilizzato nei prossimi caricamenti.</small>
    </div>
    <template #footer>
      <Button v-if="collegaDialog.item && collegaDialog.item.id_atleta" label="Scollega" icon="pi pi-times" class="p-button-text p-button-danger mr-auto" @click="confermaCollega(true)" />
      <Button label="Annulla" class="p-button-text" @click="collegaDialog.visible = false" />
      <Button label="Salva" icon="pi pi-check" :disabled="!collegaDialog.atleta || !collegaDialog.atleta.id" @click="confermaCollega(false)" />
    </template>
  </Dialog>

  <Dialog v-model:visible="deleteDialog.visible" header="Elimina tesseramento" :modal="true" :style="{ width: '400px' }">
    <p v-if="deleteDialog.item">Eliminare il tesseramento {{ labelStagione(deleteDialog.item.AnnoScadenzaContratto) }} di <b>{{ deleteDialog.item.Nome }}</b> (matricola {{ deleteDialog.item.Matricola }})?</p>
    <template #footer>
      <Button label="No" class="p-button-text" @click="deleteDialog.visible = false" />
      <Button label="Elimina" class="p-button-danger" icon="pi pi-trash" @click="confirmDelete" />
    </template>
  </Dialog>
</template>

<script>
import { ref, computed, onMounted, getCurrentInstance } from 'vue';
import Service from '@/services/levante-api/levante-api'
import Loader from '@/components/Loader.vue';
import { useToast } from 'primevue/usetoast';
import moment from 'moment'

// Punteggio minimo per preselezionare automaticamente il suggerimento migliore
const SCORE_PRESELEZIONE = 70

export default {
  components: { Loader },
  setup() {
    const self = getCurrentInstance();
    self.$eventBus = self.appContext.config.globalProperties.$eventBus
    const service = new Service(self.$eventBus);
    const toast = useToast();
    const lifeTime = 5000;

    const loading = ref(false)
    const annoCorrente = ref(moment().month() >= 6 ? moment().year() + 1 : moment().year())
    const anno = ref(annoCorrente.value)
    const stagioni = ref([])
    const tesserati = ref([])
    const senzaTessera = ref([])
    const search = ref('')
    const soloNonCollegati = ref(false)

    const fileInput = ref(null)
    const selectedFile = ref(null)
    const preview = ref(null)
    const previewVisible = ref(false)
    const sostituisciStagione = ref(false)
    const filtroStato = ref('tutti')
    const filtriStato = [
      { label: 'Tutti', value: 'tutti' },
      { label: 'Da abbinare', value: 'da_abbinare' },
      { label: 'Nuovi', value: 'nuovo' },
      { label: 'Aggiornati', value: 'aggiornato' },
      { label: 'Errori', value: 'errore' },
    ]
    const atletiSuggeriti = ref([])
    const collegaDialog = ref({ visible: false, item: null, atleta: null, aggiorna_cf: true })
    const deleteDialog = ref({ visible: false, item: null })

    const labelStagione = (a) => a ? `${a - 1}/${a}` : '-'

    const stagioniOptions = computed(() => {
      const anni = new Set(stagioni.value.map(s => Number(s.anno)))
      anni.add(annoCorrente.value)
      anni.add(annoCorrente.value + 1)
      return [...anni].sort((a, b) => b - a).map(a => ({ label: labelStagione(a), value: a }))
    })

    const nonCollegati = computed(() => tesserati.value.filter(t => !t.id_atleta).length)

    const tesseratiFiltrati = computed(() => {
      const q = search.value.trim().toLowerCase()
      return tesserati.value.filter(t =>
        (!soloNonCollegati.value || !t.id_atleta) &&
        (!q || [t.Nome, t.Matricola, t.codicefiscale, t.atleta_nome, t.atleta_cognome]
          .some(v => v && String(v).toLowerCase().includes(q))))
    })

    const daAbbinare = computed(() => preview.value ? preview.value.rows.filter(r => !r.errore && !r.id_atleta) : [])
    const abbinatiAMano = computed(() => daAbbinare.value.filter(r => r.scelta && r.scelta.id).length)

    const previewFiltrata = computed(() => {
      if (!preview.value) return []
      const rows = preview.value.rows
      if (filtroStato.value === 'tutti') return rows
      if (filtroStato.value === 'da_abbinare') return daAbbinare.value
      return rows.filter(r => r.stato === filtroStato.value)
    })

    const validRows = computed(() => preview.value ? preview.value.rows.filter(r => !r.errore).length : 0)

    const statoSeverity = (stato) => ({ nuovo: 'success', aggiornato: 'warning', errore: 'danger' }[stato] || 'info')

    const fetchStagioni = async () => {
      const res = await service.getFigcStagioni()
      stagioni.value = (res.items && res.items.stagioni) || []
    }

    const fetchData = async () => {
      loading.value = true
      try {
        const [t, s] = await Promise.all([
          service.getFigcTesserati(anno.value),
          service.getFigcSenzaTessera(anno.value),
        ])
        tesserati.value = t.items || []
        senzaTessera.value = s.items || []
      } finally {
        loading.value = false
      }
    }

    const selectAnno = (a) => {
      anno.value = Number(a)
      fetchData()
    }

    // Ricerca atleti: senza testo mostra i suggerimenti calcolati dal backend per la riga
    const cercaAtleti = async (event, row) => {
      const q = (event.query || '').trim()
      if (q.length < 2) {
        atletiSuggeriti.value = row ? [...row.suggerimenti] : []
        return
      }
      const res = await service.cercaAtletiFigc(q)
      atletiSuggeriti.value = res.items || []
    }

    const onSceltaAtleta = (row) => {
      row.preselezionato = false
      row.aggiorna_cf = !!row.scelta && row.scelta.codfis !== row.codicefiscale
    }

    const preparaAbbinamenti = (rows) => {
      rows.forEach(r => {
        r.scelta = null
        r.aggiorna_cf = false
        r.preselezionato = false
        if (r.errore || r.id_atleta || !r.suggerimenti || r.suggerimenti.length === 0) return
        const [primo, secondo] = r.suggerimenti
        if (primo.score >= SCORE_PRESELEZIONE && (!secondo || secondo.score < primo.score)) {
          r.scelta = primo
          r.preselezionato = true
          r.aggiorna_cf = primo.codfis !== r.codicefiscale
        }
      })
      return rows
    }

    const buildFormData = () => {
      const fd = new FormData()
      fd.append('file', selectedFile.value)
      fd.append('anno', anno.value)
      fd.append('sostituisci_stagione', sostituisciStagione.value ? 1 : 0)
      if (preview.value) {
        const abbinamenti = daAbbinare.value
          .filter(r => r.scelta && r.scelta.id)
          .map(r => ({ riga: r.riga, id_atleta: r.scelta.id, aggiorna_cf: !!r.aggiorna_cf }))
        fd.append('abbinamenti', JSON.stringify(abbinamenti))
      }
      return fd
    }

    const loadPreview = async (file) => {
      if (!file) return
      selectedFile.value = file
      preview.value = null
      sostituisciStagione.value = false
      loading.value = true
      try {
        const res = await service.previewFigc(buildFormData())
        if (!res || res.error) {
          toast.add({ severity: 'error', summary: 'Errore', detail: (res && res.message) || 'Impossibile leggere il file', life: lifeTime })
          return
        }
        res.rows = preparaAbbinamenti(res.rows)
        preview.value = res
        filtroStato.value = daAbbinare.value.length > 0 ? 'da_abbinare' : 'tutti'
        previewVisible.value = true
      } finally {
        loading.value = false
        if (fileInput.value) fileInput.value.value = ''
      }
    }

    const onFileSelected = (e) => loadPreview(e.target.files[0])
    const onDrop = (e) => loadPreview(e.dataTransfer.files[0])

    const annullaImport = () => {
      previewVisible.value = false
      preview.value = null
      selectedFile.value = null
    }

    const confermaImport = async () => {
      loading.value = true
      try {
        const res = await service.importFigc(buildFormData())
        if (!res || res.error) {
          toast.add({ severity: 'error', summary: 'Errore', detail: (res && res.message) || 'Importazione fallita', life: lifeTime })
          return
        }
        toast.add({ severity: 'success', summary: 'Importazione completata', detail: res.message, life: lifeTime })
        ;(res.avvisi || []).forEach(a => toast.add({ severity: 'warn', summary: 'Attenzione', detail: a, life: 10000 }))
        if (preview.value && preview.value.anni.length > 0) {
          anno.value = Math.max(...preview.value.anni)
        }
        annullaImport()
        await fetchStagioni()
        await fetchData()
      } finally {
        loading.value = false
      }
    }

    const openCollega = (item) => {
      atletiSuggeriti.value = []
      collegaDialog.value = {
        visible: true,
        item,
        atleta: item.id_atleta
          ? { id: item.id_atleta, codfis: item.atleta_codfis, label: `${item.atleta_cognome} ${item.atleta_nome} - ${item.atleta_codfis || 'CF mancante'}` }
          : null,
        aggiorna_cf: false,
      }
    }

    const confermaCollega = async (scollega) => {
      const d = collegaDialog.value
      const fd = new FormData()
      fd.append('id', d.item.id)
      fd.append('id_atleta', scollega ? '' : d.atleta.id)
      fd.append('aggiorna_cf', !scollega && d.aggiorna_cf ? 1 : 0)
      const res = await service.collegaFigc(fd)
      const ok = res.item && !res.item.error
      toast.add({ severity: ok ? 'success' : 'error', summary: 'FIGC', detail: res.item && res.item.message, life: lifeTime })
      if (ok) {
        collegaDialog.value.visible = false
        await fetchData()
      }
    }

    const askDelete = (item) => {
      deleteDialog.value = { visible: true, item }
    }

    const confirmDelete = async () => {
      const fd = new FormData()
      fd.append('id', deleteDialog.value.item.id)
      const res = await service.deleteFigc(fd)
      deleteDialog.value.visible = false
      toast.add({ severity: res.item && !res.item.error ? 'success' : 'error', summary: 'FIGC', detail: res.item && res.item.message, life: lifeTime })
      await fetchStagioni()
      await fetchData()
    }

    const goToAtleta = (id) => window.open(`/atleta/${id}/dettagli`, '_blank')

    onMounted(async () => {
      await fetchStagioni()
      await fetchData()
    })

    return {
      loading, anno, stagioni, stagioniOptions, tesserati, tesseratiFiltrati, nonCollegati, soloNonCollegati,
      senzaTessera, search, fileInput, preview, previewVisible, previewFiltrata, validRows, sostituisciStagione,
      filtroStato, filtriStato, daAbbinare, abbinatiAMano, atletiSuggeriti, collegaDialog, deleteDialog, moment,
      labelStagione, statoSeverity, fetchData, selectAnno, onFileSelected, onDrop, cercaAtleti, onSceltaAtleta,
      annullaImport, confermaImport, openCollega, confermaCollega, askDelete, confirmDelete, goToAtleta,
    }
  },
}
</script>

<style scoped>
.page-container {
  padding: 1rem;
}
</style>
